import { useI18n } from 'vue-i18n';
import { stringifyQuery } from 'vue-router';

import type { GeoRegion, PreferredRegion } from '~/utils/geo';

import { useGlobalClusters } from '~/composables/useGlobalClusters';
import { isGeoMirroredPath, UNIFIED_ROUTE_INDEX } from '~/constants/routes/unified';
import { buildClusterUrl, buildRegionSwitchUrl, omitNonTransferableQueryParams } from '~/utils/clusters';
import { parseRegionPreference, PHOENIX_GEO_STATE } from '~/utils/geo';

export interface RegionOption {
  /** Narrowed from the cluster wire format — see `regions` below. */
  geo: PreferredRegion;
  /** Absolute home URL for this region (`<scheme>://<host><pathPrefix>`). */
  url: string;
  /**
   * Absolute URL for this region preserving the current path/query
   * (non-transferable params stripped) when the path exists in every geo
   * (`GEO_MIRRORED_PUBLIC_ROUTES`), otherwise the region home with the query
   * only. Used by the geo routing switch flow, footer clicks included; only
   * the footer link's crawlable `href` keeps the plain home `url`.
   */
  switchUrl: string;
  /** Region display name (footer), e.g. `Australia`. */
  label: string;
  isCurrent: boolean;
}

/**
 * The regions a visitor can switch between — the geo routing prompts and the
 * footer region list — alongside the SSR-detected visitor region
 * (`X-Phoenix-Geo`, hydrated via `useState`), resolved against the clusters
 * this deployment knows about (`useGlobalClusters`).
 */
export const useRegionSwitcher = () => {
  const { t } = useI18n();
  const config = useRuntimeConfig();
  const route = useRoute();
  const { clusters, currentCluster } = useGlobalClusters();

  const detectedGeo = useState<GeoRegion | null>(PHOENIX_GEO_STATE, () => null);

  const scheme = config.public.useHttp ? 'http' : 'https';

  const sanitizedQuery = computed(() => omitNonTransferableQueryParams(route.query));

  // Current path + sanitized query — what analytics payloads report as `path`.
  // Matches what switchUrl itself carries: never a blacklisted param.
  const sourcePath = computed<string>(() => {
    const queryString = stringifyQuery(sanitizedQuery.value);
    return `${route.path}${queryString ? `?${queryString}` : ''}`;
  });

  // `GlobalClusterDefinition.geo` is an untyped wire string, so this is the one
  // place a region the switcher can offer is narrowed to one we actually serve;
  // a cluster with any other geo has no label, flag or preference value and is
  // dropped here rather than left for each consumer to guess at.
  const regions = computed<RegionOption[]>(() => {
    // Dynamic content has independent slugs per region, so only paths known to
    // exist in every geo carry over; everything else switches to the home page.
    const isSharedPath = isGeoMirroredPath(route.path);
    return clusters.value.flatMap((cluster) => {
      const geo = parseRegionPreference(cluster.geo);
      if (!geo) {
        return [];
      }
      return [{
        geo,
        url: buildClusterUrl(cluster.host, cluster.pathPrefix, { scheme }),
        switchUrl: buildRegionSwitchUrl(cluster.host, cluster.pathPrefix, {
          scheme,
          path: isSharedPath ? route.path : UNIFIED_ROUTE_INDEX,
          query: sanitizedQuery.value,
        }),
        label: t(`unified.geoRegion.countryName.${geo}`),
        isCurrent: geo === currentCluster.value?.geo,
      }];
    });
  });

  const currentRegion = computed<RegionOption | null>(() =>
    regions.value.find(region => region.isCurrent) ?? null,
  );

  // Presentational list for the footer's KUICoreRegionSwitcher.
  const regionOptions = computed(() =>
    regions.value.map(({ label, url, isCurrent }) => ({ label, url, isCurrent })),
  );

  return { regions, currentRegion, regionOptions, sourcePath, detectedGeo };
};
